// pages/company/SubmissionDetail.tsx
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Calendar,
  FileText,
  Download,
  MessageSquare,
  Building2,
  Mail,
  Phone,
  User,
  ChevronRight,
  Printer,
  DollarSign,
  TrendingUp,
  Briefcase,
  Cpu,
  Shield,
  Scale,
  FileSignature,
  Info,
  AlertTriangle,
  MapPin,
  Globe,
  Tag,
  Award,
  BarChart3
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { demoCompanyService, DemoSubmission } from '@/lib/demoCompanyService';
import { submissionService } from '@/lib/submissionService';

// Helper function to format currency
const formatCurrency = (value: number): string => {
  if (!value) return 'KES 0';
  if (value >= 1_000_000_000) {
    return `KES ${(value / 1_000_000_000).toFixed(2)}B`;
  }
  if (value >= 1_000_000) {
    return `KES ${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `KES ${(value / 1_000).toFixed(1)}K`;
  }
  return `KES ${value}`;
};

// Helper function to format category name
const formatCategory = (category: string): string => {
  if (!category) return '';
  return category.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

// Helper function to ensure submission has all fields with defaults for display only
const ensureDisplaySubmission = (submission: any): DemoSubmission => {
  return {
    id: submission.id || '',
    companyId: submission.companyId || '',
    companyName: submission.companyName || 'Unknown Company',
    companyRegistration: submission.companyRegistration,
    problemTitle: submission.problemTitle || 'Untitled Problem',
    description: submission.description || submission.plainDescription || 'No description provided',
    plainDescription: submission.plainDescription || submission.description || 'No description provided',
    estimatedImpact: submission.estimatedImpact || `KES ${submission.estimatedImpactValue?.toLocaleString() || 0} per year`,
    estimatedImpactValue: submission.estimatedImpactValue || 0,
    urgency: submission.urgency || 'medium',
    status: submission.status || 'pending',
    submittedAt: submission.submittedAt || new Date().toISOString(),
    reviewedAt: submission.reviewedAt,
    reviewNotes: submission.reviewNotes,
    documents: submission.documents || [],
    category: submission.category || ['other'],
    
    // Section 2: Contact Person
    contactName: submission.contactName,
    contactTitle: submission.contactTitle,
    contactEmail: submission.contactEmail,
    contactPhone: submission.contactPhone,
    contactDepartment: submission.contactDepartment,
    yearsInRole: submission.yearsInRole,
    decisionMaker: submission.decisionMaker,
    decisionMakerDetails: submission.decisionMakerDetails,
    secondaryName: submission.secondaryName,
    secondaryTitle: submission.secondaryTitle,
    secondaryEmail: submission.secondaryEmail,
    secondaryPhone: submission.secondaryPhone,

    // Section 3: Problem Description
    problemCategory: submission.problemCategory,
    otherCategory: submission.otherCategory,
    rootCause: submission.rootCause,
    firstAppeared: submission.firstAppeared,
    frequency: submission.frequency,
    seasonalMonths: submission.seasonalMonths,
    scope: submission.scope,
    previousAttempts: submission.previousAttempts,

    // Section 4: Financial Impact
    costEnergy: submission.costEnergy,
    costMaterial: submission.costMaterial,
    costDowntime: submission.costDowntime,
    costLabour: submission.costLabour,
    costCompliance: submission.costCompliance,
    costOther: submission.costOther,
    costOtherDescription: submission.costOtherDescription,
    totalAnnualCost: submission.totalAnnualCost || submission.estimatedImpactValue,
    evidence: submission.evidence || [],
    otherEvidence: submission.otherEvidence,
    confidenceLevel: submission.confidenceLevel,
    estimateDerivation: submission.estimateDerivation,

    // Section 5: Market Prevalence
    industryPrevalence: submission.industryPrevalence,
    estimatedKenya: submission.estimatedKenya,
    estimatedEastAfrica: submission.estimatedEastAfrica,
    affectedIndustries: submission.affectedIndustries,
    scalabilityFactor: submission.scalabilityFactor,
    existingSolutions: submission.existingSolutions,
    soughtHelp: submission.soughtHelp || [],

    // Section 6: Pilot Readiness
    pilotLikelihood: submission.pilotLikelihood,
    approvalAuthority: submission.approvalAuthority,
    approvalTimeline: submission.approvalTimeline,
    pilotDuration: submission.pilotDuration,
    successCriteria: submission.successCriteria,
    budgetRange: submission.budgetRange,
    commercialModel: submission.commercialModel,
    pilotAccess: submission.pilotAccess || [],

    // Section 7: Technical Environment
    facilityAge: submission.facilityAge,
    facilitySize: submission.facilitySize,
    operatingHours: submission.operatingHours,
    operatingDays: submission.operatingDays,
    monitoringSystems: submission.monitoringSystems || [],
    connectivity: submission.connectivity,
    primaryEquipment: submission.primaryEquipment,
    equipmentBrand: submission.equipmentBrand,
    equipmentAge: submission.equipmentAge,
    maintenanceSpend: submission.maintenanceSpend,
    lastOverhaul: submission.lastOverhaul,
    solutionType: submission.solutionType || [],
    technicalConstraints: submission.technicalConstraints,

    // Section 8: Regulatory Context
    regulatoryRelated: submission.regulatoryRelated,
    regulatoryBodies: submission.regulatoryBodies,
    certifications: submission.certifications || [],
    upcomingDeadlines: submission.upcomingDeadlines,

    // Section 9: Self-Assessment
    severityRating: submission.severityRating,
    urgencyRating: submission.urgencyRating,
    prevalenceRating: submission.prevalenceRating,
    dataAvailabilityRating: submission.dataAvailabilityRating,
    techOpennessRating: submission.techOpennessRating,
    managementSupportRating: submission.managementSupportRating,
    decisionSpeedRating: submission.decisionSpeedRating,
    additionalContext: submission.additionalContext,

    // Section 10: Consent
    consentStorage: submission.consentStorage,
    consentContact: submission.consentContact,
    consentAnonymised: submission.consentAnonymised,
    consentUpdates: submission.consentUpdates,
    consentSquad: submission.consentSquad,
    dataSharingLevel: submission.dataSharingLevel,
    declarationAgreed: submission.declarationAgreed,
  };
};

const CompanySubmissionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [submission, setSubmission] = useState<DemoSubmission | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [isDemoMode, setIsDemoMode] = useState(false);

  useEffect(() => {
    setIsDemoMode(submissionService.isDemoMode());
    loadSubmission();
  }, [id]);

  const loadSubmission = () => {
    setIsLoading(true);
    setTimeout(() => {
      try {
        // Try to get from localStorage first (for newly submitted)
        const localSubmissions = localStorage.getItem('demo_company_submissions');
        let data = null;
        
        if (localSubmissions) {
          const submissions = JSON.parse(localSubmissions);
          data = submissions.find((s: any) => s.id === id);
        }
        
        // If not found, try mock data from service
        if (!data) {
          data = demoCompanyService.getSubmissionById(id || '');
        }
        
        if (data) {
          // Ensure the data has all fields for display but PRESERVE real data
          const completeData = ensureDisplaySubmission(data);
          setSubmission(completeData);
        } else {
          toast({
            title: 'Error',
            description: 'Submission not found',
            variant: 'destructive'
          });
        }
      } catch (error) {
        console.error('Error loading submission:', error);
        toast({
          title: 'Error',
          description: 'Failed to load submission',
          variant: 'destructive'
        });
      }
      setIsLoading(false);
    }, 500);
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'under_review': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'accepted': return 'bg-green-100 text-green-700 border-green-200';
      case 'rejected': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'under_review': return <Eye className="h-4 w-4" />;
      case 'accepted': return <CheckCircle className="h-4 w-4" />;
      case 'rejected': return <XCircle className="h-4 w-4" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch(urgency) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 w-full max-w-full p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-10 w-96" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-48 w-full rounded-lg" />
              <Skeleton className="h-64 w-full rounded-lg" />
            </div>
            <div className="space-y-6">
              <Skeleton className="h-48 w-full rounded-lg" />
              <Skeleton className="h-64 w-full rounded-lg" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="min-h-screen bg-gray-50 w-full max-w-full p-6">
        <div className="max-w-md mx-auto">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-12 pb-12 text-center">
              <XCircle className="h-16 w-16 mx-auto mb-4 text-red-500" />
              <p className="text-xl font-semibold text-red-700 mb-2">Submission Not Found</p>
              <p className="text-sm text-red-600">The submission you're looking for doesn't exist.</p>
              <Button variant="outline" className="mt-6" onClick={() => navigate('/company/submissions')}>
                Back to Submissions
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 w-full max-w-full">
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
        {/* Breadcrumb navigation */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Button variant="ghost" onClick={() => navigate('/company/dashboard')} className="h-auto p-0 text-muted-foreground hover:text-foreground">
            Dashboard
          </Button>
          <ChevronRight className="h-3 w-3" />
          <Button variant="ghost" onClick={() => navigate('/company/submissions')} className="h-auto p-0 text-muted-foreground hover:text-foreground">
            Submissions
          </Button>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground font-medium">Submission Details</span>
        </div>

        {/* Header with title and badges */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{submission.problemTitle}</h1>
              <Badge variant="outline" className={getStatusColor(submission.status) + ' text-xs px-3 py-1'}>
                <span className="flex items-center gap-1">
                  {getStatusIcon(submission.status)}
                  {submission.status.replace('_', ' ')}
                </span>
              </Badge>
              {isDemoMode && (
                <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-200">
                  Demo Mode
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2">
              <span className="font-mono bg-muted px-2 py-0.5 rounded">Submission #{submission.id}</span>
              <span>•</span>
              <span>Submitted: {new Date(submission.submittedAt).toLocaleDateString()}</span>
              {submission.reviewedAt && (
                <>
                  <span>•</span>
                  <span>Last Updated: {new Date(submission.reviewedAt).toLocaleDateString()}</span>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => window.print()}>
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Status Banner based on submission status */}
        {submission.status === 'accepted' && (
          <Card className="overflow-hidden border-l-4 border-l-green-500 bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-green-800">Submission Accepted</h3>
                  <p className="text-sm text-green-700 mt-1">
                    Your problem has been accepted into our Problem Bank. A squad will be assigned to work on this opportunity.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {submission.status === 'rejected' && (
          <Card className="overflow-hidden border-l-4 border-l-red-500 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-red-800">Submission Not Accepted</h3>
                  <p className="text-sm text-red-700 mt-1">
                    Thank you for your submission. After review, we've determined this problem doesn't meet our current criteria.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs for all 10 steps */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border flex-wrap h-auto p-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-blue-50">Overview</TabsTrigger>
            <TabsTrigger value="contact" className="data-[state=active]:bg-blue-50">Contact</TabsTrigger>
            <TabsTrigger value="problem" className="data-[state=active]:bg-blue-50">Problem</TabsTrigger>
            <TabsTrigger value="financial" className="data-[state=active]:bg-blue-50">Financial</TabsTrigger>
            <TabsTrigger value="market" className="data-[state=active]:bg-blue-50">Market</TabsTrigger>
            <TabsTrigger value="pilot" className="data-[state=active]:bg-blue-50">Pilot</TabsTrigger>
            <TabsTrigger value="technical" className="data-[state=active]:bg-blue-50">Technical</TabsTrigger>
            <TabsTrigger value="regulatory" className="data-[state=active]:bg-blue-50">Regulatory</TabsTrigger>
            <TabsTrigger value="assessment" className="data-[state=active]:bg-blue-50">Assessment</TabsTrigger>
            <TabsTrigger value="consent" className="data-[state=active]:bg-blue-50">Consent</TabsTrigger>
          </TabsList>

          {/* Overview Tab - Summary of all sections */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Company Info Card */}
              <Card className="overflow-hidden border-l-4 border-l-blue-500">
                <CardHeader className="bg-muted/30 py-3">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-blue-600" />
                    <CardTitle className="text-base">Company Information</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <p className="font-semibold">{submission.companyName || 'Not specified'}</p>
                  {submission.companyRegistration && (
                    <p className="text-xs text-muted-foreground mt-1">Reg: {submission.companyRegistration}</p>
                  )}
                  <div className="mt-3 space-y-2">
                    {submission.contactName && (
                      <div className="flex items-center gap-2 text-sm">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span>{submission.contactName}{submission.contactTitle ? ` • ${submission.contactTitle}` : ''}</span>
                      </div>
                    )}
                    {submission.contactEmail && (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span>{submission.contactEmail}</span>
                      </div>
                    )}
                    {submission.contactPhone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span>{submission.contactPhone}</span>
                      </div>
                    )}
                    {!submission.contactName && !submission.contactEmail && !submission.contactPhone && (
                      <p className="text-sm text-muted-foreground">No contact information provided</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Problem Overview Card */}
              <Card className="overflow-hidden border-l-4 border-l-blue-500">
                <CardHeader className="bg-muted/30 py-3">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-blue-600" />
                    <CardTitle className="text-base">Problem Overview</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <p className="font-medium">{submission.problemTitle}</p>
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-3">
                    {submission.plainDescription || submission.description}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {submission.problemCategory && (
                      <Badge variant="outline" className="bg-purple-50">
                        {formatCategory(submission.problemCategory)}
                      </Badge>
                    )}
                    <Badge variant="outline" className={getUrgencyColor(submission.urgency)}>
                      {submission.urgency} urgency
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Financial Summary Card */}
              <Card className="overflow-hidden border-l-4 border-l-blue-500">
                <CardHeader className="bg-muted/30 py-3">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-blue-600" />
                    <CardTitle className="text-base">Financial Impact</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <p className="text-2xl font-bold text-red-600">
                    {formatCurrency(submission.totalAnnualCost || submission.estimatedImpactValue || 0)}
                  </p>
                  {submission.confidenceLevel && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Confidence: {submission.confidenceLevel}/5
                    </p>
                  )}
                  {submission.evidence && submission.evidence.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium mb-1">Evidence available:</p>
                      <div className="flex flex-wrap gap-1">
                        {submission.evidence.slice(0, 3).map((item, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {item.substring(0, 20)}...
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Market & Pilot Summary Card */}
              <Card className="overflow-hidden border-l-4 border-l-blue-500">
                <CardHeader className="bg-muted/30 py-3">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-blue-600" />
                    <CardTitle className="text-base">Market & Pilot</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  {submission.industryPrevalence && (
                    <p className="text-sm">{submission.industryPrevalence}</p>
                  )}
                  {submission.estimatedKenya && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Kenya market: ~{submission.estimatedKenya} facilities
                    </p>
                  )}
                  {submission.pilotLikelihood && (
                    <div className="mt-3">
                      <Badge variant="outline" className={
                        submission.pilotLikelihood.includes('Very likely') ? 'bg-green-100' :
                        submission.pilotLikelihood.includes('Likely') ? 'bg-blue-100' :
                        'bg-yellow-100'
                      }>
                        {submission.pilotLikelihood}
                      </Badge>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Documents Card */}
            {submission.documents && submission.documents.length > 0 && (
              <Card className="overflow-hidden border-l-4 border-l-blue-500">
                <CardHeader className="bg-muted/30 py-3">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-blue-600" />
                    <CardTitle className="text-base">Supporting Documents</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {submission.documents.map((doc, index) => (
                      <a
                        key={index}
                        href={doc.url}
                        className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors group"
                      >
                        <FileText className="h-5 w-5 text-blue-600" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{doc.name}</p>
                          {doc.size && (
                            <p className="text-xs text-muted-foreground">{doc.type} • {doc.size}</p>
                          )}
                        </div>
                        <Download className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </a>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Analyst Feedback Card */}
            {submission.reviewNotes && (
              <Card className="overflow-hidden border-l-4 border-l-blue-500 bg-blue-50">
                <CardHeader className="bg-blue-100/50 py-3">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-blue-600" />
                    <CardTitle className="text-base text-blue-800">Analyst Feedback</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <p className="text-sm text-blue-800 leading-relaxed">{submission.reviewNotes}</p>
                  {submission.reviewedAt && (
                    <div className="mt-4 text-xs text-blue-600">
                      Reviewed on {new Date(submission.reviewedAt).toLocaleDateString()}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Contact Tab - Step 2 */}
          <TabsContent value="contact" className="space-y-6">
            <Card className="overflow-hidden border-l-4 border-l-green-500">
              <CardHeader className="bg-muted/30 py-3">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-green-600" />
                  <CardTitle className="text-base">Primary Contact</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs text-muted-foreground">Full Name</p>
                    <p className="font-medium">{submission.contactName || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Job Title</p>
                    <p className="font-medium">{submission.contactTitle || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Email</p>
                    <p className="font-medium">{submission.contactEmail || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Phone</p>
                    <p className="font-medium">{submission.contactPhone || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Department</p>
                    <p className="font-medium">{submission.contactDepartment || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Years in Role</p>
                    <p className="font-medium">{submission.yearsInRole || 'Not specified'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground">Decision-Making Authority</p>
                    <p className="font-medium">{submission.decisionMaker || 'Not specified'}</p>
                  </div>
                  {submission.decisionMakerDetails && (
                    <div className="col-span-2">
                      <p className="text-xs text-muted-foreground">Decision Maker Details</p>
                      <p className="font-medium">{submission.decisionMakerDetails}</p>
                    </div>
                  )}
                </div>

                {(submission.secondaryName || submission.secondaryEmail) && (
                  <>
                    <Separator className="my-6" />
                    <h3 className="text-md font-semibold mb-4">Secondary Contact</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <p className="text-xs text-muted-foreground">Name</p>
                        <p className="font-medium">{submission.secondaryName || 'Not specified'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Title</p>
                        <p className="font-medium">{submission.secondaryTitle || 'Not specified'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Email</p>
                        <p className="font-medium">{submission.secondaryEmail || 'Not specified'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Phone</p>
                        <p className="font-medium">{submission.secondaryPhone || 'Not specified'}</p>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Problem Tab - Step 3 */}
          <TabsContent value="problem" className="space-y-6">
            <Card className="overflow-hidden border-l-4 border-l-purple-500">
              <CardHeader className="bg-muted/30 py-3">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-purple-600" />
                  <CardTitle className="text-base">Problem Description</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Problem Title</p>
                    <p className="text-lg font-semibold">{submission.problemTitle}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Category</p>
                    {submission.problemCategory ? (
                      <Badge variant="outline" className="mt-1 bg-purple-50">
                        {formatCategory(submission.problemCategory)}
                      </Badge>
                    ) : (
                      <p className="text-sm">Not specified</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Description</p>
                    <p className="text-sm mt-1 whitespace-pre-line">{submission.plainDescription || submission.description}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Root Cause</p>
                    <p className="text-sm mt-1">{submission.rootCause || 'Not specified'}</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">First Appeared</p>
                      <p className="font-medium">{submission.firstAppeared || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Frequency</p>
                      <p className="font-medium">{submission.frequency || 'Not specified'}</p>
                    </div>
                    {submission.seasonalMonths && (
                      <div>
                        <p className="text-xs text-muted-foreground">Seasonal Months</p>
                        <p className="font-medium">{submission.seasonalMonths}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-muted-foreground">Scope</p>
                      <p className="font-medium">{submission.scope || 'Not specified'}</p>
                    </div>
                  </div>
                  {submission.previousAttempts && (
                    <div>
                      <p className="text-xs text-muted-foreground">Previous Attempts</p>
                      <p className="text-sm mt-1">{submission.previousAttempts}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Financial Tab - Step 4 */}
          <TabsContent value="financial" className="space-y-6">
            <Card className="overflow-hidden border-l-4 border-l-red-500">
              <CardHeader className="bg-muted/30 py-3">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-red-600" />
                  <CardTitle className="text-base">Financial Impact</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs text-muted-foreground">Energy / Utilities</p>
                    <p className="font-medium">{submission.costEnergy ? formatCurrency(submission.costEnergy) : 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Material Waste</p>
                    <p className="font-medium">{submission.costMaterial ? formatCurrency(submission.costMaterial) : 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Machine Downtime</p>
                    <p className="font-medium">{submission.costDowntime ? formatCurrency(submission.costDowntime) : 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Labour / Rework</p>
                    <p className="font-medium">{submission.costLabour ? formatCurrency(submission.costLabour) : 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Compliance / Penalties</p>
                    <p className="font-medium">{submission.costCompliance ? formatCurrency(submission.costCompliance) : 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Other Costs</p>
                    <p className="font-medium">{submission.costOther ? formatCurrency(submission.costOther) : 'Not specified'}</p>
                  </div>
                </div>
                {submission.costOtherDescription && (
                  <div className="mt-2">
                    <p className="text-xs text-muted-foreground">Other Cost Description</p>
                    <p className="text-sm">{submission.costOtherDescription}</p>
                  </div>
                )}
                <Separator className="my-4" />
                <div className="bg-muted/30 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <p className="font-semibold">TOTAL ANNUAL COST</p>
                    <p className="text-2xl font-bold text-red-600">
                      {formatCurrency(submission.totalAnnualCost || submission.estimatedImpactValue || 0)}
                    </p>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Confidence Level</p>
                    <p className="font-medium">{submission.confidenceLevel ? `${submission.confidenceLevel}/5` : 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Estimate Derivation</p>
                    <p className="font-medium">{submission.estimateDerivation || 'Not specified'}</p>
                  </div>
                </div>
                {submission.evidence && submission.evidence.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-muted-foreground mb-2">Evidence Available</p>
                    <div className="flex flex-wrap gap-2">
                      {submission.evidence.map((item, i) => (
                        <Badge key={i} variant="secondary">{item}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {submission.otherEvidence && (
                  <div className="mt-4">
                    <p className="text-xs text-muted-foreground">Other Evidence</p>
                    <p className="text-sm">{submission.otherEvidence}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Market Tab - Step 5 */}
          <TabsContent value="market" className="space-y-6">
            <Card className="overflow-hidden border-l-4 border-l-orange-500">
              <CardHeader className="bg-muted/30 py-3">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-orange-600" />
                  <CardTitle className="text-base">Market Prevalence</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Industry Prevalence</p>
                    <p className="font-medium">{submission.industryPrevalence || 'Not specified'}</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Estimated Facilities in Kenya</p>
                      <p className="font-medium">{submission.estimatedKenya || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Estimated Facilities in East Africa</p>
                      <p className="font-medium">{submission.estimatedEastAfrica || 'Not specified'}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Affected Industries</p>
                    <p className="font-medium">{submission.affectedIndustries || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Existing Solutions</p>
                    <p className="font-medium">{submission.existingSolutions || 'Not specified'}</p>
                  </div>
                  {submission.soughtHelp && submission.soughtHelp.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Sought External Help</p>
                      <div className="flex flex-wrap gap-2">
                        {submission.soughtHelp.map((item, i) => (
                          <Badge key={i} variant="outline">{item}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pilot Tab - Step 6 */}
          <TabsContent value="pilot" className="space-y-6">
            <Card className="overflow-hidden border-l-4 border-l-indigo-500">
              <CardHeader className="bg-muted/30 py-3">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-indigo-600" />
                  <CardTitle className="text-base">Pilot Readiness</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Pilot Likelihood</p>
                    {submission.pilotLikelihood ? (
                      <Badge variant="outline" className="mt-1 bg-indigo-50">
                        {submission.pilotLikelihood}
                      </Badge>
                    ) : (
                      <p className="text-sm">Not specified</p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Approval Authority</p>
                      <p className="font-medium">{submission.approvalAuthority || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Approval Timeline</p>
                      <p className="font-medium">{submission.approvalTimeline || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Preferred Pilot Duration</p>
                      <p className="font-medium">{submission.pilotDuration || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Budget Range</p>
                      <p className="font-medium">{submission.budgetRange || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Commercial Model</p>
                      <p className="font-medium">{submission.commercialModel || 'Not specified'}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Success Criteria</p>
                    <p className="font-medium">{submission.successCriteria || 'Not specified'}</p>
                  </div>
                  {submission.pilotAccess && submission.pilotAccess.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Pilot Access Provided</p>
                      <div className="flex flex-wrap gap-2">
                        {submission.pilotAccess.map((item, i) => (
                          <Badge key={i} variant="secondary">{item}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Technical Tab - Step 7 */}
          <TabsContent value="technical" className="space-y-6">
            <Card className="overflow-hidden border-l-4 border-l-pink-500">
              <CardHeader className="bg-muted/30 py-3">
                <div className="flex items-center gap-2">
                  <Cpu className="h-4 w-4 text-pink-600" />
                  <CardTitle className="text-base">Technical Environment</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Facility Age</p>
                    <p className="font-medium">{submission.facilityAge ? `${submission.facilityAge} years` : 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Facility Size</p>
                    <p className="font-medium">{submission.facilitySize ? `${submission.facilitySize} sq m` : 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Operating Hours</p>
                    <p className="font-medium">{submission.operatingHours || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Operating Days/Year</p>
                    <p className="font-medium">{submission.operatingDays ? `${submission.operatingDays} days` : 'Not specified'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground mb-2">Monitoring Systems</p>
                    {submission.monitoringSystems && submission.monitoringSystems.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {submission.monitoringSystems.map((system, i) => (
                          <Badge key={i} variant="secondary">{system}</Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">None specified</p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground">Connectivity</p>
                    <p className="font-medium">{submission.connectivity || 'Not specified'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground">Primary Equipment</p>
                    <p className="font-medium">{submission.primaryEquipment || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Equipment Brand</p>
                    <p className="font-medium">{submission.equipmentBrand || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Equipment Age</p>
                    <p className="font-medium">{submission.equipmentAge ? `${submission.equipmentAge} years` : 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Maintenance Spend</p>
                    <p className="font-medium">{submission.maintenanceSpend ? formatCurrency(submission.maintenanceSpend) : 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Last Overhaul</p>
                    <p className="font-medium">{submission.lastOverhaul || 'Not specified'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground">Technical Constraints</p>
                    <p className="font-medium">{submission.technicalConstraints || 'None specified'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Regulatory Tab - Step 8 */}
          <TabsContent value="regulatory" className="space-y-6">
            <Card className="overflow-hidden border-l-4 border-l-teal-500">
              <CardHeader className="bg-muted/30 py-3">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-teal-600" />
                  <CardTitle className="text-base">Regulatory Context</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Regulatory Related</p>
                    <p className="font-medium">{submission.regulatoryRelated || 'Not specified'}</p>
                  </div>
                  {submission.regulatoryBodies && (
                    <div>
                      <p className="text-xs text-muted-foreground">Regulatory Bodies</p>
                      <p className="font-medium">{submission.regulatoryBodies}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Certifications</p>
                    {submission.certifications && submission.certifications.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {submission.certifications.map((cert, i) => (
                          <Badge key={i} variant="outline" className="bg-green-50">{cert}</Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm">None specified</p>
                    )}
                  </div>
                  {submission.upcomingDeadlines && (
                    <div>
                      <p className="text-xs text-muted-foreground">Upcoming Deadlines</p>
                      <p className="font-medium">{submission.upcomingDeadlines}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Assessment Tab - Step 9 */}
          <TabsContent value="assessment" className="space-y-6">
            <Card className="overflow-hidden border-l-4 border-l-cyan-500">
              <CardHeader className="bg-muted/30 py-3">
                <div className="flex items-center gap-2">
                  <Scale className="h-4 w-4 text-cyan-600" />
                  <CardTitle className="text-base">Self-Assessment</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-1">
                      <p className="text-xs text-muted-foreground">Financial Severity</p>
                      <span className="text-sm font-medium">{submission.severityRating ? `${submission.severityRating}/5` : 'Not specified'}</span>
                    </div>
                    {submission.severityRating && (
                      <Progress value={parseInt(submission.severityRating) * 20} className="h-2" />
                    )}
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <p className="text-xs text-muted-foreground">Urgency</p>
                      <span className="text-sm font-medium">{submission.urgencyRating ? `${submission.urgencyRating}/5` : 'Not specified'}</span>
                    </div>
                    {submission.urgencyRating && (
                      <Progress value={parseInt(submission.urgencyRating) * 20} className="h-2" />
                    )}
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <p className="text-xs text-muted-foreground">Industry Prevalence</p>
                      <span className="text-sm font-medium">{submission.prevalenceRating ? `${submission.prevalenceRating}/5` : 'Not specified'}</span>
                    </div>
                    {submission.prevalenceRating && (
                      <Progress value={parseInt(submission.prevalenceRating) * 20} className="h-2" />
                    )}
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <p className="text-xs text-muted-foreground">Data Availability</p>
                      <span className="text-sm font-medium">{submission.dataAvailabilityRating ? `${submission.dataAvailabilityRating}/5` : 'Not specified'}</span>
                    </div>
                    {submission.dataAvailabilityRating && (
                      <Progress value={parseInt(submission.dataAvailabilityRating) * 20} className="h-2" />
                    )}
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <p className="text-xs text-muted-foreground">Openness to Tech</p>
                      <span className="text-sm font-medium">{submission.techOpennessRating ? `${submission.techOpennessRating}/5` : 'Not specified'}</span>
                    </div>
                    {submission.techOpennessRating && (
                      <Progress value={parseInt(submission.techOpennessRating) * 20} className="h-2" />
                    )}
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <p className="text-xs text-muted-foreground">Management Support</p>
                      <span className="text-sm font-medium">{submission.managementSupportRating ? `${submission.managementSupportRating}/5` : 'Not specified'}</span>
                    </div>
                    {submission.managementSupportRating && (
                      <Progress value={parseInt(submission.managementSupportRating) * 20} className="h-2" />
                    )}
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <p className="text-xs text-muted-foreground">Decision Speed</p>
                      <span className="text-sm font-medium">{submission.decisionSpeedRating ? `${submission.decisionSpeedRating}/5` : 'Not specified'}</span>
                    </div>
                    {submission.decisionSpeedRating && (
                      <Progress value={parseInt(submission.decisionSpeedRating) * 20} className="h-2" />
                    )}
                  </div>
                  {submission.additionalContext && (
                    <div>
                      <p className="text-xs text-muted-foreground">Additional Context</p>
                      <p className="text-sm mt-1">{submission.additionalContext}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Consent Tab - Step 10 */}
          <TabsContent value="consent" className="space-y-6">
            <Card className="overflow-hidden border-l-4 border-l-emerald-500">
              <CardHeader className="bg-muted/30 py-3">
                <div className="flex items-center gap-2">
                  <FileSignature className="h-4 w-4 text-emerald-600" />
                  <CardTitle className="text-base">Consent & Declaration</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Data Processing Consent</p>
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${submission.consentStorage ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <span className="text-sm">Storage and processing in V&V Portal</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${submission.consentContact ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <span className="text-sm">Contact for Grey Cost Audit</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${submission.consentAnonymised ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <span className="text-sm">Share anonymised data in portfolio reporting</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${submission.consentUpdates ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <span className="text-sm">Receive updates about Problem Bank</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${submission.consentSquad ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <span className="text-sm">Assign Forge squad to develop solution</span>
                      </div>
                    </div>
                  </div>
                  <Separator />
                  <div>
                    <p className="text-xs text-muted-foreground">Data Sharing Level</p>
                    <p className="font-medium mt-1">{submission.dataSharingLevel ? submission.dataSharingLevel.replace(/-/g, ' ') : 'Not specified'}</p>
                  </div>
                  <Separator />
                  <div>
                    <p className="text-xs text-muted-foreground">Declaration</p>
                    <div className="mt-2 flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${submission.declarationAgreed ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className="text-sm font-medium">
                        {submission.declarationAgreed ? 'Declaration agreed' : 'Declaration not agreed'}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default CompanySubmissionDetail;